<template>
  <div
    class="settings-page d-flex flex-column i-px-12 i-py-11 align-items-center"
    :class="{
      'collect-config-overlay':
        $route.name.startsWith('collect-config-email-customization') ||
        $route.name.startsWith('collect-config-portal-customization'),
    }"
  >
    <div class="settings-page-heading">
      <h3
        data-testid="settings-page-title"
        class="i-text-heading-4 i-text-neutral-120 i-mb-6"
      >
        Settings
      </h3>
    </div>
    <div class="settings-page-menu i-mb-14">
      <i-tabs
        :preselected-tab="currentSelectedTab"
        @tab-selected="onTabSelected"
      >
        <template #tabs>
          <i-tab
            v-for="(tab, index) in visibleTabs"
            :key="index"
            :label="tab.label"
          ></i-tab>
        </template>
      </i-tabs>
    </div>
    <div class="d-flex justify-content-center w-100">
      <div class="settings-page-view">
        <router-view />
      </div>
    </div>
  </div>
</template>

<script>
import { iTab, iTabs } from '@inscribe/signal';
import { mapGetters } from 'vuex';

export default {
  components: { iTab, iTabs },
  data: () => ({
    tabs: [],
  }),
  computed: {
    ...mapGetters({
      isOrgAdmin: 'isOrgAdmin',
      collectEnabled: 'collectEnabled',
      isChatAgentEnabled: 'isChatAgentEnabled',
    }),
    currentSelectedTab() {
      return (
        this.visibleTabs.findIndex(
          (tab) =>
            this.$route.name === tab.name ||
            tab.altNames?.includes(this.$route.name),
        ) || 0
      );
    },
    visibleTabs() {
      return this.tabs.filter((tab) => tab.visible);
    },
  },
  watch: {
    '$route.hash': function () {
      if (this.$route.hash) {
        this.scrollToSection(this.$route.hash);
      } else {
        document.querySelector('.app-wrapper').scrollTo(0, 0);
      }
    },
    collectEnabled: function () {
      this.setupTabItems();
    },
    isChatAgentEnabled: function () {
      this.setupTabItems();
    },
  },
  mounted() {
    this.setupTabItems();
  },
  methods: {
    scrollToSection(selector) {
      const element = document.querySelector(selector);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    },
    onTabSelected(tabIndex) {
      const selectedTab = this.visibleTabs[tabIndex];
      if (selectedTab) {
        this.$router
          .push({
            name: selectedTab.name,
          })
          .catch(() => {});
      }
    },
    setupTabItems() {
      this.tabs = [
        {
          label: 'Account',
          name: 'account-settings-view',
          visible: true,
        },
        {
          label: 'Team',
          name: 'team-settings-view',
          visible: true,
        },
        {
          label: 'Collect',
          name: 'collect-config-settings-view',
          visible: this.collectEnabled,
          altNames: [
            'collect-config-email-customization',
            'collect-config-portal-customization',
          ],
        },
        {
          label: 'API Keys',
          name: 'api-settings-list-keys',
          visible: true,
          altNames: [
            'api-settings-list-keys',
            'api-settings-create-key',
            'api-settings-edit-key',
            'api-settings-view-key',
          ],
        },
        {
          label: 'Webhooks',
          name: 'webhooks-settings-list',
          visible: true,
          altNames: [
            'webhooks-settings-list',
            'webhooks-settings-create',
            'webhooks-settings-edit',
          ],
        },
        {
          label: 'Notifications',
          name: 'notifications-settings-view',
          visible: true,
        },
        {
          label: 'Single Sign-On',
          name: 'saml-settings-view',
          visible: true,
          altNames: [
            'saml-settings-view',
            'saml-settings-edit-identity-provider-view',
            'saml-settings-new-identity-provider',
          ],
        },
        {
          label: 'Assistant',
          name: 'assistant-settings-view',
          visible: this.isChatAgentEnabled,
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.settings-page {
  &-view,
  &-heading,
  &-menu {
    width: 100%;
    max-width: 1200px;
    position: sticky;
    top: 0;
    background-color: $light-neutral-default !important;
    z-index: 10;

    .more-link {
      display: none;
    }

    @include respond-to('large') {
      width: 100%;
      min-width: auto !important;

      .responsive-hide {
        display: none !important;
      }

      .more-link {
        display: list-item;
      }
    }
  }

  &-view {
    z-index: 0 !important;
  }

  .collect-config-overlay &-heading,
  .collect-config-overlay &-menu {
    display: none;
  }

  :deep(.settings-page-block) {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    @include respond-to('medium') {
      flex-direction: column;
      justify-content: flex-start;

      .settings-page-block-title {
        padding-right: 0;
        padding-bottom: 32px;
      }
    }

    .settings-page-block-title {
      width: 100%;
      padding-right: 64px;
    }

    .settings-page-block-content {
      width: 100%;
      max-width: 650px;
    }
  }
}
</style>
